import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import * as paths from '@dna/paths'
import styles from '@dna/styles'
import {
  debounce,
  getCurrencySymbol,
  handleize,
  igGetExperimentControl,
  t,
} from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  when,
} from '@lit'

/**
 * attachinternals to create a form-associated custom element is not supported in safari 16
 * The below is a polyfill and can be removed once safari 16 is no longer supported
 */
import '@polyfill/element-internals'

/**
 * Mixin that adds form-associated behavior to a custom element.
 */

export const FormAssociatedMixin = (superclass) =>
  class extends superclass {
    static formAssociated = true
    static properties = {
      filter: {
        type: Object,
      },
      selectedOptions: {
        type: Array,
        state: true,
      },
    }

    constructor() {
      super()
      this.internals_ = this.attachInternals()
      this.selectedOptions = this.value || []
      this.setFormValue(this.selectedOptions)
    }

    get value() {
      return this.selectedOptions
    }

    set value(value) {
      this.selectedOptions = value
      this.internals_.setFormValue(value)
    }

    setFormValue(value) {
      this.internals_.setFormValue(value)
      this.dispatchEvent(new Event('change', { bubbles: true }))
    }

    selectOption(option) {
      if (this.closest('sl-dropdown')) {
        this.closest('sl-dropdown').hide()
      }

      if (this.selectedOptions.includes(option.value)) {
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption) => selectedOption !== option.value,
        )
        this.setFormValue(this.selectedOptions)

        // @segment
        Segment.track(Segment.CONSTANTS.EVENTS.FILTER_REMOVED, {
          filter_name: option?.name?.toLowerCase(),
          filter_type: this.filter.display_name?.toLowerCase(),
        })
      } else {
        this.selectedOptions = [...this.selectedOptions, option.value]
        this.setFormValue(this.selectedOptions)

        // @segment
        Segment.protect(() => {
          if (this.selectedOptions.length) {
            Segment.track(Segment.CONSTANTS.EVENTS.FILTER_ADDED, {
              filter_name: option?.name?.toLowerCase(),
              filter_type: this.filter.display_name?.toLowerCase(),
            })
          }
        })
      }
    }

    handleCheckboxFocus(event) {
      const focusedCheckbox = event.target

      this.handleCheckboxKeydown = (event) => {
        if (event.key === 'Enter') {
          focusedCheckbox.click()
        }
      }

      focusedCheckbox.addEventListener('keydown', this.handleCheckboxKeydown)
    }

    handleCheckboxFocusRemoval(event) {
      const focusedCheckbox = event.target
      focusedCheckbox.removeEventListener('keydown', this.handleCheckboxKeydown)
    }
  }

/**
 * Color and Pattern filter
 */

class DNAColorFilter extends FormAssociatedMixin(LitElement) {
  static properties = {
    exposeFilters: {
      type: Boolean,
      attribute: 'expose-filters',
    },
  }

  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  render() {
    return html`
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        ${this.filter?.options?.map((option) => {
          const swatchFilename = `/cdn/shop/files/filter_${this.filter.name}_${handleize(option.value)}.png${window.__DNA_GLOBAL__?.themeJsVersion ? `?v=${window.__DNA_GLOBAL__.themeJsVersion}` : ''}`

          return html`
            <div class="color-swatch">
              <button
                class=${classMap({
                  selected: this.selectedOptions.includes(option.value),
                })}
                role="radio"
                aria-checked=${this.selectedOptions.includes(option.value)}
                @click=${() => this.selectOption(option)}
              >

                <img
                  class="color"
                  height="${this.exposeFilters ? '40' : '24'}"
                  width="${this.exposeFilters ? '40' : '24'}"
                  src="${swatchFilename}"
                  alt=""
                  loading="lazy"
                />
                <span class="label p2">${option.name} (${option.count})</span>
              </button>
            </div>
          `
        })}
      </div>
    `
  }

  static styles = [
    styles.typography,
    css`
      .radio-group {
        display: inline-grid;
        width: 100%;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px 8px;
        padding:  4px 4px;

        @media screen and (min-width: 768px) {
          gap: 20px 8px;
        }
      }

      .color-swatch button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
      }

      .color {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: cover;
      }

      .selected .color {
        outline: 1px solid var(--dna-color-midnight);
        outline-offset: 2px;
      }

      .label {
        color: var(--dna-brand-primary);
        overflow: hidden;
        text-overflow: ellipsis;
      }

      @media screen and (max-width: 1023px) {
        :host([expose-filters]) {
          .radio-group {
            display: grid;
            gap: var(--dna-space-8);
            grid-template-columns: repeat(auto-fill, minmax(var(--dna-space-20), 1fr));
            padding: 0;
          }
          .color-swatch {
            display: flex;
            justify-content: center;

            button {
              flex-direction: column;
              gap: var(--dna-space-3);
              padding: var(--dna-space-3) 0;
            }
          }
          .color {
            height: var(--dna-space-10);
            width: var(--dna-space-10);
          }
        }
      }
      
      @media screen and (min-width: 1024px) {
        :host([expose-filters]) {
          .radio-group {
            grid-template-columns: minmax(0, 1fr) auto;
          }
        }
      }
    `,
  ]
}
customElements.define('dna-color-filter', DNAColorFilter)

/**
 * Size filter
 */

class DNASizeFilter extends FormAssociatedMixin(LitElement) {
  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  render() {
    const options = this.filter?.options ?? []
    const bands = this.filter?.data?.bands

    if (!bands?.length) {
      return html`
        <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
          ${repeat(
            options,
            (option) => option.value,
            (option) => this._renderOption(option),
          )}
        </div>
      `
    }

    const groups = this._groupOptionsByBand(options, bands)

    return html`
      <div
        class=${classMap({
          'size-groups': true,
          'size-groups--grid': groups.length > 2,
        })}
        role="radiogroup"
        aria-label=${t('sections.collection_template.filter_accessible_text')}
      >
        ${repeat(
          groups,
          (group) =>
            `${group.band.key}:${group.options.map((option) => option.value).join(',')}`,
          (group) =>
            this._renderSizeGroup(group, { showLabel: groups.length >= 2 }),
        )}
      </div>
    `
  }

  _renderOption(option, { wide = false } = {}) {
    return html`
      <button
        class=${classMap({
          size: true,
          'size--wide': wide,
          selected: this.selectedOptions.includes(option.value),
        })}
        role="radio"
        aria-checked=${this.selectedOptions.includes(option.value)}
        @click=${() => this.selectOption(option)}
      >
        <span class="label p2">${option.name}</span>
      </button>
    `
  }

  /**
   * Groups options by band - options with no band prefix are hidden.
   * Only bands with at least one matching option are returned.
   */
  _groupOptionsByBand(options, bands) {
    const sortedBands = [...bands].sort((a, b) => a.position - b.position)

    return sortedBands
      .map((band) => ({
        band,
        options: options.filter((option) => {
          const [bandKey] = option.value.split(':')
          return bandKey === band.key
        }),
      }))
      .filter((group) => group.options.length > 0)
  }

  _renderSizeGroup({ band, options }, { showLabel = true } = {}) {
    const isWideBand = band.key === 'baby'

    return html`
      <div
        class=${classMap({ 'size-group': true, 'size-group--wide': isWideBand })}
        role="group"
        aria-label=${band.label}
      >
        ${showLabel ? html`<span class="size-group-label">${band.label}</span>` : nothing}
        <div class="size-group__options">
          ${repeat(
            options,
            (option) => option.value,
            (option) => this._renderOption(option, { wide: isWideBand }),
          )}
        </div>
      </div>
    `
  }

  static styles = [
    styles.typography,
    css`
      .radio-group {
        display: inline-grid;
        grid-template-columns: repeat(6, 1fr);
        justify-content: flex-start;
        gap: 8px 8px;
      }

      .size {
        width: 85px;
        height: 48px;
        background-color: transparent;
        border: 1px solid var(--dna-color-light-grey);
        box-sizing: border-box;
        cursor: pointer;

        @media screen and (min-width: 768px) {
          width: 70px;
          height: 40px;
        }
      }

      .size--wide {
        width: auto;
        min-width: 128px;
        padding: 0 var(--dna-space-3);
      }

      .selected.size {
        background-color: var(--dna-color-midnight);
      }
      .selected.size span {
        color: var(--dna-brand-reverse);
      }

      .label {
        color: var(--dna-brand-primary);
      }
      .size-groups {
        display: flex;
        flex-direction: column;
        gap: var(--dna-space-6);
      }

      :host([expose-filters]) .size-groups--grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--dna-space-6) var(--dna-space-8);
      }

      .size-group-label {
        display: block;
        font: var(--dna-body-p2-font);
        letter-spacing: 0.5px;
        color: var(--dna-brand-primary);
      }
      .size-group__options {
        display: inline-grid;
        grid-template-columns: repeat(4, max-content);
        justify-content: flex-start;
        gap: 0;
        margin-top: var(--dna-space-3);
        border-top: 1px solid var(--dna-color-light-grey);
        border-left: 1px solid var(--dna-color-light-grey);
      }

      .size-group__options .size {
        border-top: none;
        border-left: none;
      }

      .size-group--wide .size-group__options {
        grid-template-columns: repeat(2, max-content);
      }

      @media screen and (max-width: 1023px) {
        :host([expose-filters]) {
          .radio-group {
            display: grid;
            gap: 0;
            grid-template-columns: repeat(auto-fill, minmax(85px, 1fr));
            border-top: 1px solid var(--dna-color-light-grey);
            border-left: 1px solid var(--dna-color-light-grey);
          }
          .size {
            border-top: none;
            border-left: none;
            width: 85px;
          }
          .size.selected {
            border: none;
            background: var(--dna-brand-primary);

            .label {
              color: var(--dna-brand-reverse);
            }
          }
        }
      }
      @media screen and (max-width: 1023px) {
        .size-groups,
        :host([expose-filters]) .size-groups--grid {
          display: block;
        }

        .size-group {
          margin-top: var(--dna-space-4);
        }

        .size-group:first-child {
          margin-top: 0;
        }
      }
    `,
  ]
}
customElements.define('dna-size-filter', DNASizeFilter)

/**
 * Type 'Single' filter
 */

class DNASingleFilter extends FormAssociatedMixin(LitElement) {
  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  chooseOption(option) {
    const filterOption = this.filter.options.find(
      (opt) => opt.value === option.value,
    )
    this.selectedOptions = []
    option.name = filterOption?.name.toLowerCase()
    this.selectOption(option)
  }

  render() {
    const selectedOption =
      this.filter.options.find((option) =>
        this.selectedOptions.includes(option.value),
      ) || ''
    return html`
      <sl-radio-group
        class="radio-group"
        value="${selectedOption.value}"
        @sl-change=${(event) => this.chooseOption({ value: event.target.value })}
      >
        ${repeat(
          this.filter?.options || [],
          (option) => option.value,
          (option) => html`
            <sl-radio value="${option.value}">
              ${option.name} (${option.count})
            </sl-radio>
          `,
        )}
      </sl-radio-group>
    `
  }

  static styles = [
    styles.typography,
    styles.shoelace,
    css`
      .radio-group {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding:  0 4px;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .radio-group {
          gap: 16px;
        }
      }
    `,
  ]
}
customElements.define('dna-single-filter', DNASingleFilter)

/**
 * Price filter
 */

class DNAPriceFilter extends RespondToViewport(
  FormAssociatedMixin(LitElement),
) {
  static properties = {
    // @intelligems TECH-1580
    customPrice: {
      type: Object,
      state: true,
    },
    customPriceError: {
      type: Boolean,
      state: true,
    },
    minCustomPrice: {
      type: String,
      state: true,
    },
    maxCustomPrice: {
      type: String,
      state: true,
    },
    applyTest1580: {
      type: Boolean,
      state: true,
    },
    // END / @intelligems TECH-1580
  }

  constructor() {
    super()

    this.filter = {
      options: [],
    }

    // @intelligems TECH-1580
    this.applyTest1580 = false
    this.customPrice = {
      name: '',
      value: '',
    }
    this.minCustomPrice = ''
    this.maxCustomPrice = ''
    this.customPriceError = false
    this.filterPriceMinInputRef = createRef()
    this.filterPriceMaxInputRef = createRef()
    this.priceCheckboxRef = createRef()
    // END / @intelligems TECH-1580
  }

  // @intelligems TECH-1580
  get hasCustomPrice() {
    return this.filter.options.some((option) => option.count === 0)
  }
  // END / TECH-1580

  async connectedCallback() {
    super.connectedCallback()

    // @intelligems TECH-1580
    const isTECH1580Control = await igGetExperimentControl({
      staging: 'c0e5223d-9f11-4644-aba1-50e35f0b88fc',
      production: 'fa881c08-5968-4e68-9182-e06bdfdd7701',
    })
    if (!isTECH1580Control) {
      this.applyTest1580 = true

      // When collection is loaded with custom price filter already set
      if (this.hasCustomPrice) {
        this.customPrice = this.filter.options.find(
          (option) => option.count === 0,
        )
        if (this.customPrice.name) {
          const customPriceNameSplit = this.customPrice.name.split(' - ')
          this.minCustomPrice = customPriceNameSplit[0]
          this.maxCustomPrice = customPriceNameSplit[1]
        }
      }
    }
    // END / TECH-1580
  }

  chooseOption(event, option) {
    if (this.hasCustomPrice) {
      this.removeCustomPrice(event)
    }
    this.selectOption(option)
  }

  // @intelligems TECH-1580
  applyCustomPrice() {
    this.customPriceError = false

    if (this.filterPriceMaxInputRef.value.value) {
      this.maxCustomPrice = this.filterPriceMaxInputRef.value.value
    }

    if (this.filterPriceMinInputRef.value.value) {
      this.minCustomPrice = this.filterPriceMinInputRef.value.value
    } else {
      if (this.filterPriceMaxInputRef.value.value) {
        this.minCustomPrice = '0'
      }
    }

    const minNumber = Math.ceil(Number.parseFloat(this.minCustomPrice))
    const maxNumber = Math.ceil(Number.parseFloat(this.maxCustomPrice))

    if (
      this.minCustomPrice?.length &&
      minNumber >= 0 &&
      this.maxCustomPrice?.length &&
      maxNumber > 0
    ) {
      this.customPrice = {
        count: 0,
        name: `Custom: $${this.minCustomPrice} - $${this.maxCustomPrice}`,
        value: `\"${this.minCustomPrice}\"-\"${this.maxCustomPrice}\"`,
        status: '',
      }

      // Deselect any active price filters
      let selectedOptions = this.selectedOptions
      for (const selectedOption of this.selectedOptions) {
        if (selectedOption !== this.customPrice.value) {
          const option = this.filter.options.find(
            (option) => option.value === selectedOption,
          )
          selectedOptions = selectedOptions.filter(
            (selectedOption) => selectedOption !== option.value,
          )
        }
      }

      // Set custom price filter in one go instead of calling this.selectOption multiple times
      this.selectedOptions = [...selectedOptions, this.customPrice.value]
      this.setFormValue(this.selectedOptions)

      // @segment
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS.FILTER_ADDED, {
          filter_name: this.customPrice?.name?.toLowerCase(),
          filter_type: this.customPrice?.display_name?.toLowerCase(),
        })
      })
    } else {
      this.customPriceError = true
    }
  }

  removeCustomPrice(event) {
    this.customPriceError = false
    this.minCustomPrice = ''
    this.maxCustomPrice = ''

    if (event.target.closest('.js-filter-list-price-input')) {
      // When removal is a result of clicking the remove button, the form must
      // be updated even though a different option was not selected.
      this.customPrice.status = 'selected'
      this.selectOption(this.customPrice)
    } else {
      // Remove custom price from selected options
      this.selectedOptions = this.selectedOptions.filter(
        (option) => option !== this.customPrice.value,
      )
      this.setFormValue(this.selectedOptions)

      // @segment
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS.FILTER_REMOVED, {
          filter_name: this.customPrice?.name?.toLowerCase(),
          filter_type: this.customPrice?.display_name?.toLowerCase(),
        })
      })
    }
  }
  // END / @intelligems TECH-1580

  render() {
    return html`
      ${when(
        this.applyTest1580,
        () => html`
          <!-- @intelligems TECH-1580 -->
          <div class="filter-list-input--price js-filter-list-price-input">
            <sl-input
              class="filter-list-input filter-list-input--price__min"
              ?active=${this.hasCustomPrice}
              ?data-error=${this.customPriceError}
              ?data-user-valid=${this.hasCustomPrice && this.minCustomPrice.length}
              label="Min"
              min="0"
              step="1"
              type="number"
              value=${this.minCustomPrice}
              variant="floating-label"
              @focus=${(e) => e.target.setAttribute('active', '')}
              @blur=${(e) => {
                if (e.target.value.length === 0) {
                  e.target.removeAttribute('active')
                }
              }}
              ${ref(this.filterPriceMinInputRef)}
            >
              <span slot="prefix">
                ${getCurrencySymbol()}
              </span>
            </sl-input>
            <sl-input
              class="filter-list-input filter-list-input--price__max"
              ?active=${this.hasCustomPrice}
              ?data-error=${this.customPriceError}
              ?data-user-valid=${this.hasCustomPrice && this.maxCustomPrice.length}
              label="Max"
              min="1"
              step="1"
              type="number"
              value=${this.maxCustomPrice}
              variant="floating-label"
              @focus=${(e) => e.target.setAttribute('active', '')}
              @blur=${(e) => {
                if (e.target.value.length === 0) {
                  e.target.removeAttribute('active')
                }
              }}
              ${ref(this.filterPriceMaxInputRef)}
            >
              <span slot="prefix">
                $
              </span>
            </sl-input>
            ${when(
              this.hasCustomPrice &&
                this.filterPriceMinInputRef?.value?.value?.length &&
                this.filterPriceMaxInputRef?.value?.value?.length,
              () => html`
                <div class="filter-list-button">
                  <button
                    @click=${this.removeCustomPrice}
                  >
                    Remove
                  </button>
                </div>
              `,
              () => html`
                <div class="filter-list-button">
                  <button
                    @click=${this.applyCustomPrice}
                  >
                    Apply
                  </button>
                </div>
              `,
            )}
          </div>
          <p
            class="c-attention sr-only"
            role="alert"
          >
            ${when(
              this.customPriceError,
              () => html`
                Please enter valid numbers for the price inputs.  
              `,
              () => nothing,
            )}
          </p>
          <!-- END @intelligems TECH-1580 -->
        `,
        () => nothing,
      )}
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        <!-- @intelligems TECH-1580 -->
        ${when(
          this.applyTest1580 && this.hasCustomPrice,
          () => html`
            <sl-checkbox
              ${ref(this.priceCheckboxRef)}
              role="radio"
              aria-checked=${this.selectedOptions.includes(this.customPrice.value)}
              .checked=${this.selectedOptions.includes(this.customPrice.value)}
              .value="${this.customPrice.value}" name="${this.customPrice.name}"
              @sl-change=${this.removeCustomPrice}
              @sl-focus=${(event) => this.handleCheckboxFocus(event)}
              @sl-blur=${(event) => this.handleCheckboxFocusRemoval(event)}
            >
              ${when(
                this.customPrice.name.toLowerCase().includes('custom'),
                () => html`${this.customPrice.name}`,
                () => html`
                  Custom: $${this.customPrice.name.split(' - ')[0]} - $${this.customPrice.name.split(' - ')[1]}
                `,
              )}
            </sl-checkbox>
          `,
          () => nothing,
        )}
        <!-- END @intelligems TECH-1580 -->
        ${repeat(
          this.filter.options,
          (option) => option.name,
          (option) => html`
            ${when(
              option.value !== this.customPrice.value,
              () => html`
                <sl-checkbox
                  role="radio"
                  aria-checked=${this.selectedOptions.includes(option.value)}
                  .checked=${this.selectedOptions.includes(option.value)}
                  .value="${option.value}" name="${option.name}"
                  @sl-change=${(e) => this.chooseOption(e, option)}
                  @sl-focus=${(event) => this.handleCheckboxFocus(event)}
                  @sl-blur=${(event) => this.handleCheckboxFocusRemoval(event)}
                >
                  ${option.name}${option.count > 0 ? ` (${option.count})` : nothing}
                </sl-checkbox>
              `,
              () => nothing,
            )}
          `,
        )}
      </div>
    `
  }

  static styles = [
    styles.a11y,
    styles.color,
    styles.shoelace,
    styles.typography,
    css`
      /* @intelligems TECH-1580 */
      .filter-list-input,
      .filter-list-button {
        color: var(--dna-brand-primary);
        margin-bottom: var(--dna-space-6);
        max-width: unset;
      }
      .filter-list-button {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        justify-content: center;
      }

      .filter-list-input[data-error]::part(base),
      .filter-list-input[data-user-invalid]::part(base) {
        border-color: var(--dna-utility-attention) !important;
      }

      .filter-list-input[data-user-valid]::part(base) {
        border-color: var(--dna-brand-primary);
      }

      .filter-list-input::part(base) {
        box-shadow: none !important;
        height: calc(var(--dna-space-10) + var(--dna-space-2));
      }

      .filter-list-input::part(base):focus-within {
        border: 1px solid var(--dna-brand-primary);
      }

      .filter-list-input::part(input) {
        color: var(--dna-brand-primary);
        height: 100%;
        padding: var(--dna-space-3) var(--dna-space-4);
        padding-left: calc(var(--dna-space-5) / 2);
      }

      .filter-list-input::part(input)::placeholder {
        color: var(--dna-brand-primary);
      }

      .filter-list-input::part(input):focus::placeholder {
        color: transparent;
      }

      .filter-list-input--search::part(form-control-help-text) {
        margin-top: var(--dna-space-2);
      }

      .filter-list-input--price {
        display: grid;
        gap: var(--dna-space-6);
        grid-template-columns: repeat(3, minmax(0, 1fr));

        sl-input::part(form-control-label) {
          cursor: text;
          margin-left: calc(var(--dna-space-1) + var(--dna-space-0-5));
          top: calc(var(--dna-space-1) + var(--dna-space-0-5));
        }

        sl-input[active]::part(form-control-label) {
          cursor: default;
        }

        sl-input::part(prefix) {
          position: relative;
          visibility: hidden;
          z-index: 11;
        }

        sl-input[active]::part(prefix) {
          visibility: visible;
        }

        sl-input::part(input) {
          padding-left: 0;
        }

        button {
          background: transparent;
          border: none;
          color: var(--dna-color-midnight);
          cursor: pointer;
          font: var(--dna-body-p1-font);

          padding: 0;
          text-align: left;
          text-decoration: underline;
        }
      }
      /* END / @intelligems TECH-1580 */

      .radio-group {
        display: flex;
        flex-direction: column;
        gap: var(--dna-space-6);
        padding: 0 var(--dna-space-1);

        @media screen and (min-width: 768px) {
          gap: var(--dna-space-4);
        }
      }

      sl-checkbox::part(base) {
        display: inline-flex;
        gap: var(--dna-space-2);
      }

      :host([expose-filters]) {
        @media screen and (max-width: 1023px) {
          sl-checkbox::part(base) {
            gap: unset;
            flex-direction: row-reverse;
            justify-content: space-between;
          }
        }
      }

      sl-checkbox::part(label) {
        color: var(--dna-brand-primary);
        margin-inline-start: 0;
      }
    `,
  ]
}
customElements.define('dna-price-filter', DNAPriceFilter)

/**
 * Default Select filter
 */

class DNADefaultFilter extends RespondToViewport(
  FormAssociatedMixin(LitElement),
) {
  static properties = {
    showAllOptions: { type: Boolean },
    // @intelligems TECH-1580
    hasSearched: {
      type: Boolean,
      state: true,
    },
    searchQuery: {
      type: String,
      state: true,
    },
    applyTest1580: {
      type: Boolean,
      state: true,
    },
    // END / @intelligems TECH-1580
  }

  constructor() {
    super()

    this.filter = {
      options: [],
    }
    this.showAllOptions = false

    // @intelligems TECH-1580
    this.applyTest1580 = false
    this.hasSearched = false
    this.filterSearchInputRef = createRef()
    // END / @intelligems TECH-1580
  }

  // @intelligems TECH-1580
  get searchableOptions() {
    const options = this.initialOptions ?? this.filter?.options ?? []
    const query = this.searchQuery?.toLowerCase().trim()

    if (!query) return options

    const filteredOptions = options.filter((option) => {
      return option.name?.toLowerCase()?.trim()?.includes(query)
    })

    return filteredOptions.length ? filteredOptions : options
  }

  get searchInputHelpText() {
    const query = this.searchQuery?.toLowerCase().trim()
    const options = this.initialOptions ?? this.filter?.options ?? []
    const hasResults = options.some((option) => {
      return option.name?.toLowerCase()?.trim()?.includes(query)
    })

    if (!this.hasSearched || !query || hasResults) return ''

    return `${t('general.search.no_results_found_for')} ${this.searchQuery}`
  }
  // END / @intelligems TECH-1580

  get visibleOptions() {
    const initialOptions = this.searchableOptions.slice(6)
    const selectedHiddenInitialOption = initialOptions.some((option) =>
      this.selectedOptions.includes(option.value),
    )
    if (selectedHiddenInitialOption || this.showAllOptions) {
      this.showAllOptions = true
      return this.searchableOptions
    }
    return this.searchableOptions
      .filter((option) => option.count > 0)
      .slice(0, 6)
  }

  async connectedCallback() {
    super.connectedCallback()

    // @intelligems TECH-1580
    const isTECH1580Control = await igGetExperimentControl({
      staging: 'c0e5223d-9f11-4644-aba1-50e35f0b88fc',
      production: 'fa881c08-5968-4e68-9182-e06bdfdd7701',
    })
    if (!isTECH1580Control) {
      this.applyTest1580 = true
      this.initialOptions = this.filter.options
    }
    // END / TECH-1580
  }

  // @intelligems TECH-1580
  willUpdate(changedProperties) {
    if (changedProperties.has('filter') && this.filter?.options) {
      this.initialOptions = this.filter.options
    }
  }
  // END / TECH-1580

  chooseOption(option) {
    this.selectOption(option)
  }

  // @intelligems TECH-1580
  handleSearchInput = debounce(() => {
    this.hasSearched = true
    this.searchQuery = this.filterSearchInputRef.value.value
  }, 400)
  // END / @intelligems TECH-1580

  toggleShowAllOptions() {
    this.showAllOptions = true
  }

  render() {
    return html`
      <!-- @intelligems TECH-1580 -->
      ${when(
        this.applyTest1580 && this.initialOptions?.length > 6,
        () => html`
          <sl-input
            class="filter-list-input filter-list-input--search js-filter-list-search-input"
            placeholder="Search for a ${this.filter.display_name?.toLowerCase()}"
            help-text=${this.searchInputHelpText}
            @sl-input=${this.handleSearchInput}
            ${ref(this.filterSearchInputRef)}
          >
            <span slot="prefix">
              ${paths.search}
            </span>
          </sl-input>
        `,
        () => nothing,
      )}
      <!-- END @intelligems TECH-1580 -->
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        ${repeat(
          this.visibleOptions,
          (option) => option.name,
          (option) => html`
           <sl-checkbox
            role="radio"
            aria-checked=${this.selectedOptions.includes(option.value)}
            .checked=${this.selectedOptions.includes(option.value)}
            .value="${option.value}" name="${option.name}"
            @sl-change=${() => this.chooseOption(option)}
            @sl-focus=${(event) => this.handleCheckboxFocus(event)}
            @sl-blur=${(event) => this.handleCheckboxFocusRemoval(event)}
           >
            ${option.name}${option.count > 0 ? ` (${option.count})` : nothing}
          </sl-checkbox>
          `,
        )}
        ${when(
          this.searchableOptions.length > 6 && !this.showAllOptions,
          () => html`
            <button class="view-more-button" @click=${this.toggleShowAllOptions}>
              ${t('sections.collection_template.view_more_button')}
            </button>
          `,
          () => nothing,
        )}
      </div>
    `
  }

  static styles = [
    styles.shoelace,
    styles.typography,
    css`
      /* @intelligems TECH-1580 */
      .filter-list-input {
        color: var(--dna-brand-primary);
        margin-bottom: var(--dna-space-6);
        max-width: unset;
      }

      .filter-list-input[data-error]::part(base),
      .filter-list-input[data-invalid]::part(base) {
        border-color: var(--dna-utility-attention) !important;
      }

      .filter-list-input::part(base) {
        box-shadow: none !important;
        height: calc(var(--dna-space-10) + var(--dna-space-2));
      }

      .filter-list-input::part(base):focus-within {
        border: 1px solid var(--dna-brand-primary);
      }

      .filter-list-input::part(input) {
        color: var(--dna-brand-primary);
        height: 100%;
        padding: var(--dna-space-3) var(--dna-space-4);
        padding-left: calc(var(--dna-space-5) / 2);
      }

      .filter-list-input--search [slot="prefix"] {
        margin: 0;
        padding: var(--dna-space-3) var(--dna-space-4);
        padding-right: 0;
      }

      .filter-list-input::part(input)::placeholder {
        color: var(--dna-brand-primary);
      }

      .filter-list-input::part(input):focus::placeholder {
        color: transparent;
      }

      .filter-list-input--search::part(form-control-help-text) {
        margin-top: var(--dna-space-2);
      }

      .radio-group {
        display: flex;
        flex-direction: column;
        gap: var(--dna-space-6);
        padding: 0 var(--dna-space-1);

        @media screen and (min-width: 768px) {
          gap: var(--dna-space-4);
        }
      }

      sl-checkbox::part(base) {
        display: inline-flex;
        gap: var(--dna-space-2);
      }

      :host([expose-filters]) {
        @media screen and (max-width: 1023px) {
          sl-checkbox::part(base) {
            gap: unset;
            flex-direction: row-reverse;
            justify-content: space-between;
          }
        }
      }

      sl-checkbox::part(label) {
        color: var(--dna-brand-primary);
        margin-inline-start: 0;
      }

      .view-more-button {
        background: transparent;
        border: none;
        text-decoration: underline;
        padding: 0;
        text-align: left;
        cursor: pointer;
        color: var(--dna-color-midnight);
      }
    `,
  ]
}
customElements.define('dna-default-filter', DNADefaultFilter)

class DNAGridFilters extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    filters: {
      type: Array,
    },
    hidePriceControlOptions: {
      type: Boolean,
    },
    exposeFilters: {
      type: Boolean,
      attribute: 'expose-filters',
    },
    productCount: {
      type: String,
    },
    selectedBucketFilter: {
      type: Object,
      state: true,
    },
  }

  $form = createRef()

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.filters = []
    this.hidePriceControlOptions = false
    this.router = __DNA_GLOBAL__.utils.queryRouter
    this.scrolling = false
    this.exposedFilterFirstScroll = false
    this.selectedBucketFilter = {}
  }

  connectedCallback() {
    super.connectedCallback()
    this.$filterableGrid = document.querySelector('dna-constructor-results')
  }

  _handleFormChange(event) {
    event.preventDefault()
    const formData = new FormData(this.$form.value)
    const data = Object.fromEntries(formData.entries())

    const prefixedData = Object.keys(data).reduce((acc, key) => {
      acc[`filter.${key}`] = data?.[key] ? data[key].split(',') : []
      return acc
    }, {})

    this.router.query.update({
      ...prefixedData,
      page: 1, // On filter change, reset the page to 1
    })
  }

  _handleDropdownOpen(event) {
    this.$filterableGrid.toggleFilters()

    this.$dropdown = event.target
    const $sizeFilter = this.$dropdown.querySelector('dna-size-filter')
    const $colorFilter = this.$dropdown.querySelector('dna-color-filter')
    const $defaultFilter = this.$dropdown.querySelector('dna-default-filter')
    this.$sizeFilterButtons =
      $sizeFilter?.renderRoot?.querySelectorAll('button')
    this.$colorFilterButtons =
      $colorFilter?.renderRoot?.querySelectorAll('button')
    this.$defaultFilterButtons =
      $defaultFilter?.renderRoot?.querySelectorAll('sl-checkbox')
    this.$defaultFilterInputs = []
    if (this.$defaultFilterButtons) {
      this.$defaultFilterInputs = Array.from(this.$defaultFilterButtons).map(
        ($button) => {
          return $button.renderRoot.querySelector('input')
        },
      )
    }

    this.dropdownItemIndex = 0
    this.$dropdownItems =
      this.$sizeFilterButtons ||
      this.$colorFilterButtons ||
      this.$defaultFilterInputs ||
      []
    this.$firstDropdownItem = this.$dropdownItems[0]
    this.$lastDropdownItem = this.$dropdownItems[this.$dropdownItems.length - 1]
    this.firstDropdownItemFocused = false
    this.lastDropdownItemFocused = false

    this.$dropdown.addEventListener('sl-hide', () => {
      this.firstDropdownItemFocused = false
      this.lastDropdownItemFocused = false
      this.dropdownItemIndex = 0
      this.$dropdown.removeEventListener('keydown', this._handleDropdownKeydown)
      this.$dropdown.removeEventListener('keydown', this._handleDropdownKeydown)
      this.$dropdown.removeEventListener('keydown', this._handleDropdownKeydown)
    })

    if (this.$sizeFilterButtons)
      this.$dropdown.addEventListener('keydown', this._handleDropdownKeydown)
    if (this.$colorFilterButtons)
      this.$dropdown.addEventListener('keydown', this._handleDropdownKeydown)
    if (this.$defaultFilterInputs)
      this.$dropdown.addEventListener('keydown', this._handleDropdownKeydown)
  }

  _handleDropdownKeydown = (event) => {
    if (event.key === 'Escape') {
      this.$dropdown.hide()
    } else {
      if (event.key === 'ArrowRight') {
        if (!this.firstDropdownItemFocused && this.dropdownItemIndex === 0) {
          this.firstDropdownItemFocused = true
          this.$firstDropdownItem.focus()
        } else {
          if (this.dropdownItemIndex === this.$dropdownItems.length - 1) {
            this.lastDropdownItemFocused = true
            this.dropdownItemIndex = 0
            this.firstDropdownItemFocused = true
            this.$firstDropdownItem.focus()
          } else {
            this.dropdownItemIndex += 1
            this.$dropdownItems[this.dropdownItemIndex].focus()
          }
        }
      }
      if (event.key === 'ArrowLeft') {
        if (
          !this.lastDropdownItemFocused &&
          this.dropdownItemIndex === this.$dropdownItems.length - 1
        ) {
          this.dropdownItemIndex = this.$dropdownItems.length - 1
          this.lastDropdownItemFocused = true
          this.$lastDropdownItem.focus()
        } else {
          if (this.dropdownItemIndex === 0) {
            this.firstDropdownItemFocused = true
            this.dropdownItemIndex = this.$dropdownItems.length - 1
            this.lastDropdownItemFocused = true
            this.$lastDropdownItem.focus()
          } else {
            this.dropdownItemIndex -= 1
            this.$dropdownItems[this.dropdownItemIndex].focus()
          }
        }
      }
    }
  }

  _formatSizeString(filter) {
    let sizeRegion
    if (Object.keys(filter).length) {
      if (filter.display_name.includes('(')) {
        sizeRegion = filter.display_name
          .slice(
            filter.display_name.indexOf('('),
            filter.display_name.indexOf(')') + 1,
          )
          ?.toUpperCase()
      }
    }
    return filter ? `${filter.name.toLowerCase()} ${sizeRegion}` : ''
  }

  showFilter(filter, selected) {
    if (Object.keys(filter).length) {
      if (filter.type === 'single') {
        return html`
          <dna-single-filter ?expose-filters=${this.exposeFilters} .filter=${filter} name="${filter.name}" .value=${selected}></dna-single-filter>
        `
      }
      const filterName = filter.name.toLowerCase().startsWith('price')
        ? 'price'
        : filter.name

      switch (filterName) {
        case 'color':
        case 'pattern':
          return html`
            <dna-color-filter ?expose-filters=${this.exposeFilters} .filter=${filter} name="${filter.name}" .value=${selected}></dna-color-filter>
          `
        case 'size':
          return html`
            <dna-size-filter ?expose-filters=${this.exposeFilters} .filter=${filter} name="${filter.name}" .value=${selected}></dna-size-filter>
          `
        case 'price':
          return html`
            <dna-price-filter ?expose-filters=${this.exposeFilters} .filter=${filter} name="${filter.name}" .value=${selected}></dna-price-filter>
          `
        default:
          return html`
            <dna-default-filter ?expose-filters=${this.exposeFilters} .filter=${filter} name="${filter.name}" .value=${selected}></dna-default-filter>
          `
      }
    }
  }

  render() {
    return html`
      <div class="container">
        <form
          ${ref(this.$form)}
          class="${classMap({
            'filter-group': true,
            'filter-group--buckets': this.exposeFilters,
          })}"
          @change=${this._handleFormChange}
        >
          ${repeat(
            this.filters,
            (filter) => filter.display_name,
            (filter, index) => {
              if (
                !filter?.options ||
                (this.hidePriceControlOptions &&
                  filter.display_name === 'PRICE')
              ) {
                return nothing
              }
              const selectedValues = filter?.options
                ?.filter((option) => {
                  return option.status === 'selected'
                })
                .map((option) => option.value)

              return html`
                ${when(
                  this.exposeFilters,
                  () => html`
                    ${when(
                      this.isMobile && index < 3,
                      () => html`
                        <sl-button
                          @click=${() => {
                            this.selectedBucketFilter = filter
                            const $selectedFilterShelf =
                              this.renderRoot.querySelector(
                                `.filter-shelf[label*="${filter.name}"]`,
                              )
                            if ($selectedFilterShelf)
                              $selectedFilterShelf.show()
                          }}
                          class="filter__dropdown-button"
                          slot="trigger"
                          variant=""
                          size="medium"
                        >
                          ${when(
                            filter.name === 'size',
                            () => this._formatSizeString(filter),
                            () => filter.display_name.toLowerCase(),
                          )}
                          <div slot="suffix">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" part="svg">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        </sl-button>
                        <sl-drawer
                          class="filter-shelf"
                          label="${filter.name}"
                          placement="bottom"
                        >
                          ${this.showFilter(filter, selectedValues)}
                          <div
                            slot="footer"
                            class="filter-shelf__buttons"
                          >
                            <sl-button
                              variant="secondary"
                              @click=${this.$filterableGrid.clearAllFilters}
                            >
                              ${t('sections.collection_template.clear.all')}
                            </sl-button>
                            <sl-button
                              variant="primary"
                              @click=${(e) => {
                                e.target.closest('.filter-shelf').hide()
                              }}
                            >
                              ${t('sections.collection_template.total_products').replace('{{ count }}', this.productCount)}
                            </sl-button>
                          </div>
                        </sl-drawer>
                      `,
                      () =>
                        when(
                          index < 3,
                          () => html`
                          <sl-dropdown
                            class="filter__dropdown"
                            data-filter=${filter.name}
                            @sl-show=${(e) => this._handleDropdownOpen(e)}
                          >
                            <sl-button
                              class="filter__dropdown-button"
                              slot="trigger"
                              variant=""
                              size="medium"
                            >
                              ${when(
                                filter.name === 'size',
                                () => this._formatSizeString(filter),
                                () => filter.display_name.toLowerCase(),
                              )}
                              <div slot="suffix">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" part="svg">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                            </sl-button>
                            <div class="filter__dropdown-content flex wrap">
                              ${this.showFilter(filter, selectedValues)}
                            </div>
                          </sl-dropdown>
                        `,
                        ),
                    )}
                  `,
                  () => html`
                    <sl-details
                      ?open=${index === 0}
                      value=${selectedValues}
                    >
                      <div class="filter-group__summary" slot="summary">${filter.display_name}</div>
                      <span slot="expand-icon">
                        ${paths.plus}
                      </span>
                      <span slot="collapse-icon">
                        ${paths.minus}
                      </span>
                      ${this.showFilter(filter, selectedValues)}
                    </sl-details>
                  `,
                )}
              `
            },
          )}
        </form>
      </div>
    `
  }

  static styles = [
    styles.container,
    styles.shoelace,
    styles.typography,
    css`
      * {
        box-sizing: border-box;
      }

      .container {
        padding-left: var(--dna-space-5);
        padding-right: var(--dna-space-5);
      }

     sl-details::part(base) {
        border: none;
        border-top: 1px solid var(--dna-color-light-grey);
        border-radius: 0;
        background-color: transparent;
      }

      sl-details:first-child::part(base) {
        border-top: none;
      }

      sl-details::part(summary-icon) {
        /* Disable the expand/collapse animation */
        rotate: none;
        font-size: 24px;
      }

      sl-details::part(header) {
        padding: var(--dna-space-4) 0;
        --sl-focus-ring: 1px solid var(--dna-color-focus);
        --sl-focus-ring-offset: -2px;
        font: var(--dna-label-m-font);
        letter-spacing: 2px;
        text-transform: uppercase;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 1024px) {
        sl-details::part(header) {
          font: var(--dna-label-s-font)
        }
      }

      sl-details::part(content) {
        padding: var(--dna-space-2) 0 var(--dna-space-8) 0;
      }
      .filter-group__summary {
        font-weight: normal;
        margin: 0;
      }

      .filter-group--buckets {
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-start;
        gap: calc(var(--dna-space-5) / 2);
        min-height: unset;
        overflow-x: auto;
        scrollbar-width: none;

        @media screen and (min-width: 1024px) {
          align-items: center;
          gap: var(--dna-space-4);
          height: var(--dna-space-10);
          justify-content: center;
        }
      }
      .filter-group--buckets::-webkit-scrollbar {
        width: 10px;
      }
      .filter-group--buckets::-webkit-scrollbar-track {
        background: var(--dna-color-light-grey);
      }

      .filter-group--buckets::-webkit-scrollbar-thumb {
        background: var(--dna-color-neutral);
        border-radius: 5px;
      }

      .filter-group--buckets::-webkit-scrollbar-thumb:hover {
        background: var(--dna-color-subdued);
      }
      .filter__dropdown::part(panel) {
        position: relative;
        top: var(--dna-space-5);
      }
      .filter__dropdown-content {
        background-color: var(--dna-brand-reverse);
        box-shadow: 0 var(--dna-space-1) var(--dna-space-1) rgba(0, 0, 0, 0.25);
        gap: calc(var(--dna-space-5) / 2);
        padding: var(--dna-space-6) var(--dna-space-4);
      }
      .filter_dropdown li {
        cursor: pointer;
      }
      .filter__dropdown[data-filter="size"] {
        li {
          aspect-ratio: 1;
          border: 1px solid var(--dna-color-light-grey);
          font: var(--dna-body-p2-font);
          width: var(--dna-space-10);
          align-items: center;
          justify-content: center;
        }
      }
      .filter__dropdown[data-filter="color"] {
        li {
          flex: 1 0 calc(50% - calc(var(--dna-space-5) / 2));
        }
      }
      .filter__dropdown-button {
        padding: 0;

        @media screen and (min-width: 1024px) {
          padding: 0 var(--dna-space-2);
        }
      }
      .filter__dropdown-button::part(base) {
        align-items: center;
        background-color: var(--dna-color-sandstone);
        border: none;
        color: var(--dna-brand-primary);
        font: var(--dna-body-p2-font);
        font-size: 13px;
        min-height: var(--dna-space-6);
        padding: 0;
        padding-right: var(--dna-space-3);
        padding-inline-end: 0;

        @media screen and (min-width: 1024px) {
          font: var(--dna-body-p1-font);
        }
      }
      .filter__dropdown-button::part(base):hover {
        color: var(--dna-brand-primary);
      }
      .filter__dropdown-button::part(label) {
        font: var(--dna-body-p3-font);
        font-size: 13px;
        letter-spacing: 0.5px;
        line-height: var(--dna-space-5);
        padding: 0;
        padding-right: var(--dna-space-1);
        text-transform: capitalize;

        @media screen and (min-width: 1024px) {
          font: var(--dna-body-p1-font);
          padding-right: var(--dna-space-2);
        }
      }
      .filter__dropdown-button [slot="suffix"] {
        height: var(--dna-space-4);
        width: var(--dna-space-4);
        padding: 0;
      }
      .filter__dropdown[open] [slot="suffix"] {
        transform: rotate(180deg);
      }

      :host([expose-filters]) {
        overflow-x: auto;

        @media screen and (min-width: 1024px) {
          overflow-x: unset;
        }

        .container {
          padding: 0;
        }
      }
      dna-size-filter[expose-filters] {
        position: relative;
        margin-bottom: var(--dna-space-8);
      }
      sl-drawer::part(header) {
        padding: 0 0 var(--dna-space-6) var(--dna-space-4);
      }
      sl-drawer::part(title) {
        font: var(--dna-h3-font);
        padding: 0;
        padding-top: var(--dna-space-6);
        text-transform: capitalize;
      }
      sl-drawer::part(panel) {
        height: auto;
        max-height: 485px;
      }
      sl-drawer::part(body) {
        padding: 0 var(--dna-space-4) var(--dna-space-10);
      }
      sl-drawer::part(header-actions) {
        padding: 0;
      } 
      sl-drawer::part(close-button__base) {
        align-self: flex-start;
        font-size: 24px;
        margin: 0;
        padding: var(--dna-space-5);
      }
      sl-drawer::part(footer) {
        padding: 0 var(--dna-space-4) var(--dna-space-10);
      }
      .filter-shelf {
        -z-index: 
      }
      .filter-shelf__buttons {
        display: flex;
        gap: var(--dna-space-2);
        background: var(--dna-brand-reverse);

        sl-button {
          flex: 1 1 auto;
        }
        sl-button::part(label) {
          font: var(--dna-label-m-font);
          padding: 0;
        }
        sl-button[variant="secondary"]::part(base) {
          letter-spacing: unset;
          text-transform: unset;
        }
      }
    `,
  ]
}

customElements.define('dna-grid-filters', DNAGridFilters)
